import { useCallback, useEffect, useMemo, useState, type VoidFunctionComponent } from 'react';

import { useParams } from '../../../contexts/RouterContext';
import { window } from '../../../globals';
import { useFetchPolicy, useUpdatePolicy } from '../../../hooks/api';
import useIsLicenseConcludedEnabled from '../../../hooks/features/useIsLicenseConcludedEnabled';
import { Action, ResourceType, useHasPermission } from '../../../shared/permissions';
import Button, { BUTTON_SIZE } from '../../generic/Button';
import Card from '../../generic/Card/Card';
import Flex from '../../generic/Flex';
import List from '../../generic/List';
import Loader from '../../generic/Loader';
import EditPolicyCTA from '../EditPolicyCTA';
import type { PolicyVersion } from '../../../../shared/model-typings';
import type { AsyncStatus } from '../../../types';

import LicensePolicySettingItem from './LicensePolicySettingItem';
import { LicensePolicyPageSubnav } from './LicensePolicySubnav';

import styles from './LicensePolicySettingsPage.module.less';

/**
 * These keys need to match the PolicyVersion setting field names.
 */
type SettingKey = keyof Pick<
  PolicyVersion,
  'intelligentAutoIgnoreEnabled' | 'onlyConcludedLicensesEnabled' | 'unconcludedIssueScanningEnabled'
>;

type SettingsState = Record<SettingKey, boolean>;

type SettingsConfig = {
  key: SettingKey;
  label: string;
  description: string;
  initialValue: boolean;
  isDisabled?: (settings: SettingsState) => boolean;
  disabledTooltip?: string;
};

const SETTINGS_CONFIG: SettingsConfig[] = [
  {
    key: 'onlyConcludedLicensesEnabled',
    label: 'Only create issues for Concluded Licenses',
    description: 'Reduce issue noise by only creating issues for packages\' concluded licenses.',
    initialValue: false,
  },
  {
    key: 'intelligentAutoIgnoreEnabled',
    label: 'Intelligent Auto-Ignore',
    description: 'When enabled, licensing issues from non-concluded licenses will be automatically ignored.',
    initialValue: false,
    // This setting is disabled if "Only create issues for Concluded Licenses" is enabled
    isDisabled: (settings) => settings.onlyConcludedLicensesEnabled,
    disabledTooltip: 'This setting requires "Only create issues for Concluded Licenses" to be disabled.',
  },
  {
    key: 'unconcludedIssueScanningEnabled',
    label: 'Create issues when a license can\'t be concluded',
    description:
      'A unique "Unconcluded" issue will be created when FOSSA can\'t automatically determine a license to conclude.',
    initialValue: false,
  },
];

const DisplayLicensePolicyRulesPage: VoidFunctionComponent = () => {
  const organizationId = window.user?.organization?.id;
  const isLicenseConcludedEnabled = useIsLicenseConcludedEnabled(organizationId as number);
  const params = useParams<{ policyId: number }>();

  const { data: policy, isLoading: isLoadingPolicy } = useFetchPolicy(params.policyId);

  const { mutate: updatePolicy, isPending: isUpdatingPolicy } = useUpdatePolicy(params.policyId, () => {
    setOriginalSettings(currentSettings);
  });

  const hasPermission = useHasPermission();

  const canEditPolicy =
    hasPermission({ action: Action.Edit, resourceType: ResourceType.LicensingPolicyAny }) ||
    hasPermission({ action: Action.Create, resourceType: ResourceType.LicensingPolicy });

  // Initialize settings state from config, resetting whenever the policy is updated or loads.
  const initialSettings = useMemo(() => {
    const settings: SettingsState = {} as SettingsState;
    SETTINGS_CONFIG.forEach(({ key, initialValue }) => {
      settings[key] = policy?.latestVersion?.[key] ?? initialValue;
    });
    return settings;
  }, [policy]);

  const [currentSettings, setCurrentSettings] = useState<SettingsState>(initialSettings);
  const [originalSettings, setOriginalSettings] = useState<SettingsState>(initialSettings);

  // Reset currentSettings and originalSettings when initialSettings changes.
  useEffect(() => {
    setCurrentSettings(initialSettings);
    setOriginalSettings(initialSettings);
  }, [initialSettings]);

  // Check if any settings have changed since the last save (or initial load).
  const hasChanges = useMemo(() => {
    return Object.keys(currentSettings).some(
      (key) => currentSettings[key as SettingKey] !== originalSettings[key as SettingKey],
    );
  }, [currentSettings, originalSettings]);

  const handleSettingChange = useCallback((key: SettingKey, value: boolean) => {
    setCurrentSettings((prev) => {
      const newSettings = { ...prev, [key]: value };

      // Handle settings that are dependent on other settings.
      // If only-concluded licenses is enabled, disable intelligent auto-ignore
      if (key === 'onlyConcludedLicensesEnabled' && value) {
        newSettings.intelligentAutoIgnoreEnabled = false;
      }

      return newSettings;
    });
  }, []);

  const handleSave = useCallback(() => {
    updatePolicy({
      onlyConcludedLicensesEnabled: currentSettings.onlyConcludedLicensesEnabled,
      intelligentAutoIgnoreEnabled: currentSettings.intelligentAutoIgnoreEnabled,
      unconcludedIssueScanningEnabled: currentSettings.unconcludedIssueScanningEnabled,
    });
  }, [currentSettings, updatePolicy]);

  const isSettingEnabled = useCallback(
    (setting: SettingsConfig): boolean => {
      return !setting.isDisabled?.(currentSettings);
    },
    [currentSettings],
  );

  const isLoading = isLoadingPolicy || isUpdatingPolicy;

  if (!policy) {
    return <Loader />;
  }

  return (
    <>
      <EditPolicyCTA policy={policy} status={{ isLoading } as AsyncStatus} />
      <LicensePolicyPageSubnav policyId={params.policyId} activeKey="settings" />
      <div className={styles.container}>
        <Card isFullWidth>
          <Card.Header>
            <Flex justify="space-between">
              <div className={styles.title}>License Policy Settings</div>
              <div className={styles.saveButton}>
                <Button
                  size={BUTTON_SIZE.MEDIUM}
                  onClick={handleSave}
                  isDisabled={!hasChanges || isLoading || !isLicenseConcludedEnabled}
                >
                  <div className={styles.saveButtonText}>Save</div>
                </Button>
              </div>
            </Flex>
          </Card.Header>
          <Card.Content>
            <List isBorderless>
              {SETTINGS_CONFIG.map((setting) => {
                const isEnabled = isSettingEnabled(setting) && canEditPolicy;
                const hasCurrentValue = currentSettings[setting.key];
                return (
                  <LicensePolicySettingItem
                    key={setting.key}
                    label={setting.label}
                    description={setting.description}
                    initialValue={setting.initialValue}
                    currentValue={hasCurrentValue}
                    isDisabled={!isEnabled || !isLicenseConcludedEnabled}
                    disabledTooltip={
                      canEditPolicy ? setting.disabledTooltip : 'You do not have permission to edit this policy.'
                    }
                    onChange={(value) => handleSettingChange(setting.key, value)}
                    isLoading={isLoading}
                  />
                );
              })}
            </List>
          </Card.Content>
        </Card>
      </div>
    </>
  );
};

export default DisplayLicensePolicyRulesPage;
